import { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { brendoClient } from '../api/brendoApi';
import PageShell from '../components/layout/PageShell';
import ExpressionDialog from '../components/results/ExpressionDialog';
import ResultsTable from '../components/results/ResultsTable';

export default function TransmembranePage({ client = brendoClient }) {
  const [state, setState] = useState({ status: 'loading', rows: [], error: null });
  const [retryToken, setRetryToken] = useState(0);
  const [selectedGene, setSelectedGene] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', rows: [], error: null });
    client.fetchTransmembraneFocus()
      .then((rows) => {
        if (!cancelled) setState({ status: 'success', rows, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ status: 'error', rows: [], error });
      });
    return () => {
      cancelled = true;
    };
  }, [client, retryToken]);

  const openExpression = useCallback((id, name) => setSelectedGene({ id, name }), []);

  return (
    <PageShell
      eyebrow="Unified prioritization"
      title="Transmembrane Focus"
      intro="RRA-prioritized transmembrane genes are ordered by the four-list RRA ranking derived from DESeq2 and WRST analyses across full and selected tissue scopes. Use method and bootstrap badges as supporting evidence, then inspect expression magnitude and external references."
      actions={<Button component={RouterLink} to="/methods" variant="outlined">How the four-list RRA ranking is calculated</Button>}
    >
      <Box sx={{ mb: 3, p: 2.5, borderRadius: 2, bgcolor: '#edf7f5', border: '1px solid #d2e9e4' }}>
        <Typography sx={{ fontWeight: 800 }}>Reading this table</Typography>
        <Typography color="text.secondary">
          The Four-list RRA score determines the four-list RRA ranking. Aggregate and bootstrap badges describe method-specific support; protein and perfusion fields are contextual annotations and do not alter the Four-list RRA score.
        </Typography>
      </Box>

      {state.status === 'loading' && (
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="center" sx={{ py: 10 }}>
          <CircularProgress size={26} />
          <Typography>Loading Transmembrane Focus genes…</Typography>
        </Stack>
      )}
      {state.status === 'error' && (
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={() => setRetryToken((value) => value + 1)}>Retry</Button>}
        >
          The Transmembrane Focus genes could not be loaded.
        </Alert>
      )}
      {state.status === 'success' && !state.rows.length && <Alert severity="info">No Transmembrane Focus genes are available.</Alert>}
      {state.status === 'success' && state.rows.length > 0 && (
        <ResultsTable kind="transmembraneFocus" rows={state.rows} onExpression={openExpression} />
      )}

      <ExpressionDialog
        open={Boolean(selectedGene)}
        gene={selectedGene}
        onClose={() => setSelectedGene(null)}
        client={client}
      />
    </PageShell>
  );
}
